<script lang="ts">
  /**
   * The conversation index — one row per thread, newest activity first.
   *
   * `selectable` is the only thing the two layouts change here: in two panes the
   * open row stays highlighted because it is still on screen beside its thread, and
   * in one pane it never is, because opening replaced the list entirely. Same rows,
   * same order, one class.
   */
  import Avatar from '../Avatar.svelte';
  import StateBlock from '../StateBlock.svelte';
  import { skoolToPlain } from '../skoolContent';
  import { timeAgo, type ConversationView } from './types';

  let {
    conversations,
    openId = $bindable(null),
    loading = false,
    error = null,
    selectable = false,
    empty,
    now = Date.now(),
  }: {
    conversations: ConversationView[];
    openId?: string | null;
    loading?: boolean;
    error?: string | null;
    selectable?: boolean;
    empty?: import('svelte').Snippet | undefined;
    now?: number;
  } = $props();
</script>

{#if error}
  <StateBlock state="error" message={error} compact />
{:else if loading && conversations.length === 0}
  <StateBlock state="loading" message="Loading conversations…" compact />
{:else if conversations.length === 0}
  {#if empty}
    {@render empty()}
  {:else}
    <StateBlock state="empty" message="No conversations yet." compact />
  {/if}
{:else}
  <ul class="space-y-1">
    {#each conversations as c (c.id)}
      {@const active = selectable && c.id === openId}
      <li>
        <button
          type="button"
          class="flex w-full items-start gap-2 rounded-box border p-2 text-left transition-colors
                 {active ? 'border-primary/40 bg-primary/5' : 'hair bg-base-100 hover:bg-base-200/60'}"
          onclick={() => (openId = c.id)}
        >
          <Avatar src={c.pictureUrl} name={c.name} size={32} memberId={c.memberId ?? null} />
          <span class="min-w-0 flex-1">
            <span class="flex items-baseline gap-1.5">
              <span class="redact min-w-0 flex-1 truncate text-xs font-semibold">{c.name}</span>
              {#if c.meta}
                <span class="shrink-0 font-mono text-[10px] tabular-nums opacity-55">{c.meta}</span>
              {/if}
              <span class="shrink-0 text-[10px] opacity-50">{timeAgo(c.lastAt, now)}</span>
            </span>
            <!-- The preview is deliberately PLAIN, but not RAW. Rendering the markup
                 here would put interactive elements inside a button and wrap a link
                 in a link — the thread is where content is content — while printing
                 it unparsed shows the owner `[https://x](https://x)`, which is how
                 this looked before. `skoolToPlain` is the same flattening the post
                 excerpts use. -->
            <span class="mt-0.5 flex items-center gap-1.5">
              <span class="min-w-0 flex-1 truncate text-[11px] opacity-65">{c.preview ? skoolToPlain(c.preview) : '—'}</span>
              {#if c.unread > 0}
                <span class="badge badge-primary badge-xs shrink-0">{c.unread}</span>
              {/if}
            </span>
          </span>
        </button>
      </li>
    {/each}
  </ul>
{/if}
